/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import {
  hasGroups,
  isApprovedOwner,
  isApprovedSeller,
  USER_TYPES,
  isGuestUser,
} from '../../../../util/userHelpers';

// Set to true to reactivate the "Browse Services" mobile menu link
const SHOW_SERVICES_LINK = false;

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    onLogout,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const isApproved = useMemo(() => isApprovedSeller(currentUser), [currentUser]);
  const isOwner = useMemo(() => isApprovedOwner(currentUser), [currentUser]);
  const userHasGroups = useMemo(() => hasGroups(currentUser), [currentUser]);
  const isGuest = useMemo(() => isGuestUser(currentUser), [currentUser]);
  const isLightspeedAuthorized =
    currentUser?.attributes?.profile?.privateData?.lightspeedConfig?.authorized === true;
  const cartItemCount = useSelector(state =>
    (state.cart?.items || []).reduce((sum, i) => sum + i.quantity, 0)
  );

  if (!isAuthenticated) {
    const browseProducts = (
      <NamedLink name="SearchPage" className={css.navigationLink}>
        <FormattedMessage id="TopbarMobileMenu.browseProductsLink" />
      </NamedLink>
    );

    const browseServices = (
      <NamedLink name="ServicesSearchPage" className={css.navigationLink}>
        <FormattedMessage id="TopbarMobileMenu.browseServicesLink" />
      </NamedLink>
    );

    const providerSignup = (
      <NamedLink name="BecomeASellerPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.providerSignupLink" />
      </NamedLink>
    );

    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          {browseProducts}
          {SHOW_SERVICES_LINK && browseServices}
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>
          <div className={css.authenticationGreeting}>{providerSignup}</div>
        </div>
        {/* <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div> */}
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes?.profile?.firstName || '';
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage
            id={isGuest ? 'TopbarMobileMenu.guestGreeting' : 'TopbarMobileMenu.greeting'}
            values={{ displayName }}
          />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        {/* Hide all navigation links for guests */}
        {!isGuest && (
          <>
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('SearchPage'))}
              name="SearchPage"
            >
              <FormattedMessage id="TopbarMobileMenu.browseProductsLink" />
            </NamedLink>
            {SHOW_SERVICES_LINK && (
              <NamedLink
                className={classNames(css.navigationLink, currentPageClass('ServicesSearchPage'))}
                name="ServicesSearchPage"
              >
                <FormattedMessage id="TopbarMobileMenu.browseServicesLink" />
              </NamedLink>
            )}
            <NamedLink
              className={classNames(css.inbox, currentPageClass('InboxPage'))}
              name="InboxPage"
              params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
            >
              <FormattedMessage id="TopbarMobileMenu.inboxLink" />
              {notificationCountBadge}
            </NamedLink>
            {isApproved ? (
              <NamedLink
                className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
                name="ManageListingsPage"
              >
                <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
              </NamedLink>
            ) : null}
            {/* DISABLED: Cart link hidden for guests — original below
            <a
              href="/cart"
              className={classNames(css.navigationLink, currentPage === 'CartPage' ? css.currentPage : null)}
            >
              Cart{cartItemCount > 0 ? ` (${cartItemCount})` : ''}
            </a>
            */}
            {/* DISABLED: Cart link hidden
            {!isGuest && (
              <a
                href="/cart"
                className={classNames(css.navigationLink, currentPage === 'CartPage' ? css.currentPage : null)}
              >
                Cart{cartItemCount > 0 ? ` (${cartItemCount})` : ''}
              </a>
            )}
            */}
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('BookmarkedListingsPage'))}
              name="BookmarkedListingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.bookmarkedListingsLink" />
            </NamedLink>
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
            </NamedLink>
            {/* <NamedLink
              className={classNames(css.navigationLink, currentPageClass('BusinessProfileSettingsPage'))}
              name="BusinessProfileSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.businessProfileSettingsLink" />
            </NamedLink> */}
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
            </NamedLink>
            {isOwner ? (
              <NamedLink
                className={classNames(css.navigationLink, currentPageClass('InviteUsersPage'))}
                name="InviteUsersPage"
              >
                <FormattedMessage id="UserNav.teamInvites" />
              </NamedLink>
            ) : null}
            {userHasGroups ? (
              <NamedLink
                className={classNames(css.navigationLink, currentPageClass('GroupsPage'))}
                name="GroupsPage"
              >
                <FormattedMessage id="UserNav.yourTeams" />
              </NamedLink>
            ) : null}
            {/* Seller-only embed portal tabs, mirrored from desktop UserNav */}
            {isApproved ? (
              <>
                <NamedLink
                  className={classNames(
                    css.navigationLink,
                    currentPageClass('SellerStatisticsPage')
                  )}
                  name="SellerStatisticsPage"
                >
                  <FormattedMessage id="UserNav.statistics" />
                </NamedLink>
                <NamedLink
                  className={classNames(
                    css.navigationLink,
                    currentPageClass('SellerWebsiteAdminPage')
                  )}
                  name="SellerWebsiteAdminPage"
                >
                  <FormattedMessage id="UserNav.websiteAdmin" />
                </NamedLink>
                {isLightspeedAuthorized ? (
                  <NamedLink
                    className={classNames(
                      css.navigationLink,
                      currentPageClass('LightspeedIntegrationPage')
                    )}
                    name="LightspeedIntegrationPage"
                  >
                    <FormattedMessage
                      id="UserNav.lightspeedIntegration"
                      defaultMessage="Lightspeed"
                    />
                  </NamedLink>
                ) : null}
              </>
            ) : null}
            <div className={css.spacer} />
          </>
        )}
      </div>
      {/* Hide create listing footer for guests */}
      {isApproved && !isOwner && !isGuest ? (
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      ) : null}
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
